import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SwapStatus } from '@layerswap/widget-types';
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { Form, useFormikContext } from "formik";
import { Loader2 } from "lucide-react";
import { Widget } from "../../../../../components/Widget/Index";
import { useValidationContext } from "../../../../../context/validationContext";
import useWallet from "../../../../../hooks/useWallet";
import { useProvidersConnectReady } from "@layerswap/wallet-core";
import { Address as AddressClass } from "../../../../../lib/address/Address";
import useAutoSourceRoute from "../../../../../hooks/useAutoSourceRoute";
import { useSwapDataState, useSwapDataUpdate } from "../../../../../context/swap";
import { useInitialSettings, useSettingsState } from "../../../../../context/settings";
import { useSelectedAccount } from "../../../../../context/swapAccounts";
import { generateSwapInitialValues } from "../../../../../lib/generateSwapInitialValues";
import { TransactionType } from "../../../../../lib/apiClients/layerSwapApiClient";
import EasyDepositBanner from "./EasyDepositBanner";
import PayFromPicker from "./PayFromPicker";
import ReceivePicker from "./ReceivePicker";
import DepositAddressInfo from "./DepositAddressInfo";
import DepositAddressFormButton from "./DepositAddressFormButton";
import { DepositAddressTransition } from "./DepositAddressTransition";
import { resolveDepositAddress } from "../../../../../helpers/depositActions";
import { useConnectModal } from "../../../../../components/Wallet/WalletModal";
// `Processing` is the post-deposit progress UI. It only renders when the
// user has already submitted a swap and is mid-flow — on first paint of
// `/` it is always inactive. Lazy-loading keeps the Withdraw component
// graph (transferProcessing, multi-step wallet UI, etc.) out of the home
// page's entry chunks.
const Processing = lazy(() => import(/* webpackChunkName: "swap-processing" */ "../../Withdraw/Processing"));
const SwapSummary = lazy(() => import("../../Withdraw/Summary"));
import ValidationError from "../SecondaryComponents/validationError";
import { ErrorDisplay } from "../SecondaryComponents/validationError/ErrorDisplay";
import FailIcon from "../../../../../components/Icons/FailIcon";
const DepositAddressForm = ({ disableAutoConnect, hideDestinationPicker, lockDestinationAddress, hideEasyDepositBanner, hidePoweredBy, destinationPicker }) => {
    const { values, isSubmitting, setFieldValue, submitForm } = useFormikContext();
    const { to: destination, destination_address, toAsset: toCurrency, from, fromAsset } = values || {};
    const { isAutoSourceUpdating } = useAutoSourceRoute();
    const { wallets } = useWallet();
    const hasWallet = wallets.length > 0;
    // Until every live provider is ready (wagmi auto-reconnect, starknet
    // enumeration, etc.), `wallets` may be empty even though a persisted wallet
    // is about to appear — so keep showing the loading state in that window.
    const providersReady = useProvidersConnectReady();
    const { connect, isWalletModalOpen, cancel } = useConnectModal();
    const settings = useSettingsState();
    const initialSettings = useInitialSettings();
    const connectedAutofillNetworks = useMemo(() => {
        const set = new Set();
        wallets.forEach(w => {
            w.autofillSupportedNetworks?.forEach(n => set.add(n.toLowerCase()));
        });
        return set;
    }, [wallets]);
    // When the user lands on this flow with no wallet at all, force a
    // non-dismissable connect modal — there's nothing for the picker to show
    // until at least one wallet is connected. The modal is torn down when
    // either a wallet appears (cleanup) or the form unmounts (user navigated
    // away). `isWalletModalOpen` is read via a ref so its updates don't
    // re-trigger this effect and cancel our just-opened modal.
    const isWalletModalOpenRef = useRef(isWalletModalOpen);
    useEffect(() => { isWalletModalOpenRef.current = isWalletModalOpen; });
    useEffect(() => {
        if (disableAutoConnect)
            return;
        if (hasWallet)
            return;
        if (isWalletModalOpenRef.current)
            return;
        connect(undefined, {
            placement: 'widget-body',
            dismissible: false,
            topContent: _jsx(EasyDepositBanner, { variant: "modal", currentStepIndex: 0 }),
            fullHeight: true,
            hideHeader: true,
        });
        return () => { cancel(); };
    }, [hasWallet, connect, cancel, disableAutoConnect]);
    // Apply default destination/source when a wallet is present and the form
    // is still blank. `generateSwapInitialValues` is the same helper used by
    // Formik's initialValues, so the deposit-address flow lands on the same
    // defaults whether the wallet was present from mount or connected later.
    useEffect(() => {
        if (!hasWallet || destination)
            return;
        const defaults = generateSwapInitialValues(settings, initialSettings, 'deposit-address', connectedAutofillNetworks);
        if (!defaults.to || !defaults.toAsset)
            return;
        setFieldValue('to', defaults.to, false);
        setFieldValue('toAsset', defaults.toAsset, true);
    }, [hasWallet, destination, settings, initialSettings, connectedAutofillNetworks, setFieldValue]);
    // Auto-fill `destination_address` from the current default wallet account
    // for the chosen destination. The picker can override this by calling
    // `selectDestinationAccount`, which updates `destinationAccount` and feeds
    // back through this effect (so the picker's choice wins). Manually-added
    // addresses (carried over from other flows via `selectedDestAccounts`) are
    // intentionally ignored here — this flow expects a connected wallet.
    const rawDestinationAccount = useSelectedAccount("to", destination?.name);
    const destinationAccount = rawDestinationAccount?.id === 'manually_added' ? undefined : rawDestinationAccount;
    useEffect(() => {
        if (lockDestinationAddress)
            return;
        if (!destination)
            return;
        const next = destinationAccount?.address ?? '';
        if ((destination_address ?? '').toLowerCase() === next.toLowerCase())
            return;
        setFieldValue('destination_address', next, true);
    }, [lockDestinationAddress, destination?.name, destinationAccount?.address, destination_address, setFieldValue]);
    useEffect(() => {
        setFieldValue('depositMethod', 'deposit_address', true);
    }, []);
    const { formValidation } = useValidationContext();
    const { swapId, swapBasicData, swapDetails, depositActionsResponse, refuel, swapError, swapDetailsError, depositActionsError, setSwapError } = useSwapDataState();
    const { setSwapId, mutateSwap, mutateDepositActions } = useSwapDataUpdate();
    const [isRetrying, setIsRetrying] = useState(false);
    const isValid = !formValidation.message;
    const error = formValidation.message;
    const allFieldsReady = !!(from && fromAsset && destination && toCurrency && destination_address);
    const swapMatchesValues = useMemo(() => {
        if (!swapId || !swapBasicData || !allFieldsReady)
            return false;
        return (swapBasicData.source_network?.name === from?.name &&
            swapBasicData.source_token?.symbol === fromAsset?.symbol &&
            swapBasicData.destination_network?.name === destination?.name &&
            swapBasicData.destination_token?.symbol === toCurrency?.symbol &&
            AddressClass.equals(swapBasicData.destination_address ?? '', destination_address ?? '', destination));
    }, [swapId, swapBasicData, from, fromAsset, destination, toCurrency, destination_address, allFieldsReady]);
    // Auto-create the swap once form is complete. The ref tracks the last
    // attempted (from, fromAsset, to, toAsset, address) tuple so we don't loop:
    // if the just-created swap is then dropped by the stale-swap effect (e.g.
    // because the API echoes the address in a different canonical form), the
    // same key must not be auto-resubmitted. Cleared by explicit user actions
    // like "Deposit more", and whenever the form drops to incomplete (e.g.
    // wallet disconnects) so reconnecting the same wallet re-creates the swap.
    const attemptedKeyRef = useRef(null);
    const fieldKey = allFieldsReady
        ? `${from?.name}|${fromAsset?.symbol}|${destination?.name}|${toCurrency?.symbol}|${destination_address?.toLowerCase()}`
        : null;
    // Preserve swaps whose route is unknown. A submitted snapshot can establish
    // a route mismatch before the details request finishes.
    useEffect(() => {
        if (!swapId || !swapBasicData || swapMatchesValues)
            return;
        if (swapDetails && swapDetails.id !== swapId)
            return;
        setSwapId(undefined);
        if (fieldKey && attemptedKeyRef.current === fieldKey) {
            setSwapError?.('The deposit does not match your selection. Please try again.');
        }
    }, [swapId, swapBasicData, swapDetails, swapMatchesValues, fieldKey, setSwapId, setSwapError]);
    useEffect(() => {
        if (!fieldKey) {
            attemptedKeyRef.current = null;
            return;
        }
        if (swapId || isSubmitting || !isValid)
            return;
        if (isAutoSourceUpdating)
            return;
        if (attemptedKeyRef.current === fieldKey)
            return;
        attemptedKeyRef.current = fieldKey;
        submitForm();
    }, [fieldKey, swapId, isSubmitting, isValid, submitForm, isAutoSourceUpdating]);
    const depositAddress = resolveDepositAddress(from, depositActionsResponse);
    // Show the Processing panel once the swap has moved past the
    // "user-needs-to-send" phase (i.e. any status other than created /
    // user_transfer_pending). Until then, keep showing the QR + fees.
    const isPostUserTransferStatus = !!swapDetails?.status
        && swapDetails.status !== SwapStatus.UserTransferPending
        && swapDetails.status !== SwapStatus.Created;
    const isProcessing = !!swapId && swapMatchesValues && isPostUserTransferStatus;
    // The Processing panel renders "Transfer complete" as soon as an output
    // transaction exists (resolveSwapPhase's `outputReady`: a hash + amount) —
    // even before the output tx status string flips to "completed". Mirror that
    // exact signal here so the "Deposit more" button appears at the same moment
    // the panel shows the deposit as done, instead of waiting for a status the
    // panel never gates on.
    const outputTx = swapDetails?.transactions?.find(t => t.type === TransactionType.Output);
    const hasOutputTx = !!(outputTx?.transaction_hash && outputTx?.amount);
    const isCompleted = !!swapId && swapMatchesValues && hasOutputTx;
    const showDepositInfo = !!swapId && swapMatchesValues && !isProcessing;
    const depositError = swapError || swapDetailsError || depositActionsError
        || (showDepositInfo && depositActionsResponse && !depositAddress ? 'No deposit address was returned. Please try again.' : undefined);
    const retryDeposit = async () => {
        if (isSubmitting || isRetrying || !fieldKey || !isValid)
            return;
        setIsRetrying(true);
        setSwapError?.(null);
        try {
            if (swapId) {
                // A failed read must not create another swap or change its id.
                // SWR retains any new failure so the retry remains available.
                await Promise.allSettled([mutateSwap(), mutateDepositActions()]);
            }
            else {
                attemptedKeyRef.current = fieldKey;
                await submitForm();
            }
        }
        finally {
            setIsRetrying(false);
        }
    };
    // Clear the cached attempt key so re-selecting the same route re-creates the swap.
    const resetSwap = (network, token) => {
        const isSameSource = from?.name === network?.name && fromAsset?.symbol === token?.symbol;
        if (isSameSource) {
            if (!swapId)
                attemptedKeyRef.current = null;
            return;
        }
        attemptedKeyRef.current = null;
        setSwapError?.(null);
        setSwapId(undefined);
    };
    const content = isProcessing ? (_jsx(Suspense, { fallback: null, children: _jsx(Processing, {}) })) : (_jsx(Widget.Content, { children: _jsx("div", { className: "w-full flex flex-col justify-between flex-1 relative min-h-60", children: _jsxs("div", { className: "flex flex-col w-full gap-3", children: [!hideEasyDepositBanner && _jsx(EasyDepositBanner, {}), !disableAutoConnect && !providersReady && !hasWallet ? (_jsxs("div", { className: "flex items-center justify-center gap-2 py-12 text-sm text-secondary-text", children: [_jsx(Loader2, { className: "h-4 w-4 animate-spin" }), _jsx("span", { children: "Loading wallets\u2026" })] })) : (_jsxs(_Fragment, { children: [_jsx(PayFromPicker, { selectedSource: from && fromAsset ? { network: from, token: fromAsset } : null, onSourceChange: (network, token) => {
                                    resetSwap(network, token);
                                    setFieldValue('from', network, false);
                                    setFieldValue('fromAsset', token, true);
                                }, destinationNetwork: destination?.name, destinationToken: toCurrency?.symbol, hideDestinationPicker: hideDestinationPicker && !destinationPicker }), destinationPicker
                                ? destinationPicker
                                : !hideDestinationPicker && (_jsx(ReceivePicker, { selectedDestination: destination && toCurrency ? { network: destination, token: toCurrency } : null, onDestinationChange: (network, token) => {
                                        resetSwap(network, token);
                                        setFieldValue('to', network, false);
                                        setFieldValue('toAsset', token, true);
                                    }, destinationAddress: destination_address, destination: destination })), !depositError && (showDepositInfo || lockDestinationAddress) && (_jsx(DepositAddressInfo, { sourceNetwork: from, sourceToken: fromAsset, destinationNetwork: destination, destinationToken: toCurrency, destinationAddress: destination_address, refuel: !!refuel || !!swapBasicData?.refuel, depositAddress: depositAddress, isCreatingSwap: !showDepositInfo }))] })), _jsx(ValidationError, {}), depositError && (_jsx("div", { role: "alert", children: _jsx(ErrorDisplay, { icon: _jsx(FailIcon, { width: 20, height: 20 }), title: "Couldn't generate deposit address", message: depositError }) }))] }) }) }));
    const actions = (_jsx(Widget.Footer, { showPoweredBy: !hidePoweredBy, children: _jsx(DepositAddressFormButton, { values: values, isValid: isValid, error: error, isSubmitting: isSubmitting || isRetrying, showDepositInfo: showDepositInfo, depositAddress: depositAddress, isProcessing: isProcessing, isCompleted: isCompleted, hasDepositError: !!depositError, onRetry: retryDeposit, onDepositMore: resetSwap }) }));
    return (_jsx(Form, { className: "h-full grow flex flex-col flex-1 justify-between w-full gap-3", children: _jsx(DepositAddressTransition, { summary: isProcessing && _jsx(Suspense, { fallback: null, children: _jsx(SwapSummary, {}) }), instructions: !isProcessing && _jsxs(_Fragment, { children: [content, actions] }), processing: isProcessing && _jsxs(_Fragment, { children: [content, actions] }) }) }));
};
export default DepositAddressForm;
